import { useState } from 'react';
import { useInstallPrompt } from '../lib/install';
import {
  DEFAULT_REMINDER_MINUTE,
  disableReminder,
  enableReminder,
  formatMinute,
  reminderSupported,
  useReminder,
  type ReminderResult,
} from '../lib/reminder';
import { Button } from './Button';

const MESSAGES: Record<Exclude<ReminderResult, 'on'>, string> = {
  denied: 'Notifications are blocked for this site. Allow them in your browser settings first.',
  unsupported: 'Reminders aren’t available in this browser.',
  failed: 'That didn’t work. Please try again.',
};

/** Hours offered for the reminder (local time). */
const HOURS = Array.from({ length: 17 }, (_, i) => (i + 6) * 60);

/** Results screen: one-tap "remind me tomorrow" after the daily round. */
export function ReminderCard() {
  const minute = useReminder();
  const [state, setState] = useState<'idle' | 'busy' | ReminderResult>('idle');
  if (!reminderSupported() || (minute !== null && state !== 'on')) return null;
  if (state === 'on') {
    return (
      <p role="status" className="rounded-xl bg-white/5 px-3 py-2 text-sm text-emerald-300">
        🔔 Done. We’ll remind you at {formatMinute(minute ?? DEFAULT_REMINDER_MINUTE)} each day you
        haven’t played yet. Change it in Settings.
      </p>
    );
  }
  return (
    <div className="rounded-2xl bg-white/5 p-4" data-testid="reminder-card">
      <div className="font-semibold">🔔 Don’t lose your streak</div>
      <p className="mt-0.5 text-sm text-white/60">
        Get one notification a day when you haven’t played yet. Nothing else.
      </p>
      {state !== 'idle' && state !== 'busy' && (
        <p className="mt-2 text-xs text-red-300">{MESSAGES[state]}</p>
      )}
      <Button
        size="sm"
        variant="secondary"
        className="mt-3"
        disabled={state === 'busy'}
        onClick={async () => {
          setState('busy');
          setState(await enableReminder(DEFAULT_REMINDER_MINUTE));
        }}
      >
        Remind me daily at {formatMinute(DEFAULT_REMINDER_MINUTE)}
      </Button>
    </div>
  );
}

/** Settings: turn the reminder on or off and pick the time. */
export function ReminderSettings() {
  const minute = useReminder();
  const install = useInstallPrompt();
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(DEFAULT_REMINDER_MINUTE);

  const apply = async (m: number): Promise<void> => {
    setBusy(true);
    const res = await enableReminder(m);
    setBusy(false);
    setNote(res === 'on' ? `Reminder set for ${formatMinute(m)}.` : MESSAGES[res]);
  };

  return (
    <fieldset className="mb-5 rounded-xl bg-white/5 p-3" data-testid="reminder-settings">
      <legend className="px-1 text-sm text-white/70">Daily reminder</legend>
      {!reminderSupported() ? (
        <p className="text-xs text-white/50">
          {install.iosHint
            ? 'On iPhone, add Geosportle to your Home Screen first (Share, then “Add to Home Screen”), open it from there and turn reminders on here.'
            : 'Reminders aren’t available in this browser.'}
        </p>
      ) : (
        <>
          <p className="text-xs text-white/50">
            One notification a day, only if you haven’t played yet.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <select
              aria-label="Reminder time"
              value={minute ?? pending}
              disabled={busy}
              onChange={(e) => {
                const m = Number(e.target.value);
                // While off, picking a time only chooses it; "Turn on" asks for permission.
                if (minute === null) setPending(m);
                else void apply(m);
              }}
              className="min-h-10 rounded-lg bg-white/10 px-2 text-sm text-white"
            >
              {HOURS.map((m) => (
                <option key={m} value={m} className="bg-ink-2">
                  {formatMinute(m)}
                </option>
              ))}
            </select>
            {minute === null ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() => void apply(pending)}
              >
                Turn on
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={async () => {
                  await disableReminder();
                  setNote('Reminder off.');
                }}
              >
                Turn off
              </Button>
            )}
          </div>
          {note && (
            <p role="status" className="mt-1 text-xs text-white/70">
              {note}
            </p>
          )}
        </>
      )}
    </fieldset>
  );
}
